/**
 * App shell. Routes:
 *   /                  — canvas (gated by useSession; redirects to /signup/1 if unsigned)
 *   /signup/1..6       — parent sign-up flow (MKO-101, spec §11.3.3)
 *   /teacher           — public marketing page for the school product (Phase 2)
 *   /teacher/sign-in   — teacher sign-in
 *   /teacher/dashboard — protected dashboard (Authoring / Live / Review)
 *   /join              — student join-by-code entry
 *
 * Email verification link from the COPPA email-plus chain points at the
 * api at /api/auth/verify-email; better-auth handles it server-side and
 * issues a cookie before redirecting to PUBLIC_APP_URL — no /verify
 * route needed on the web side.
 */

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Root } from './routes/Root.tsx';
import { PrivacyPage } from './routes/legal/Privacy.tsx';
import { TermsPage } from './routes/legal/Terms.tsx';
import { Step1Handoff } from './routes/signup/Step1Handoff.tsx';
import { Step2Provider } from './routes/signup/Step2Provider.tsx';
import { Step3Consent } from './routes/signup/Step3Consent.tsx';
import { Step4Child } from './routes/signup/Step4Child.tsx';
import { Step5Avatar } from './routes/signup/Step5Avatar.tsx';
import { Step6Done } from './routes/signup/Step6Done.tsx';
import { TeacherDashboard } from './routes/teacher/Dashboard.tsx';
import { StudentJoin } from './routes/teacher/Join.tsx';
import { TeacherLanding } from './routes/teacher/Landing.tsx';
import { TeacherSignIn } from './routes/teacher/SignIn.tsx';
import { AuthoringTab } from './routes/teacher/dashboard/Authoring.tsx';
import { LiveTab } from './routes/teacher/dashboard/Live.tsx';
import { ReviewTab } from './routes/teacher/dashboard/Review.tsx';
import { BuildersComingSoon, InventorsComingSoon } from './routes/tiers/ComingSoon.tsx';
import { TierPicker } from './routes/tiers/TierPicker.tsx';

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Root />} />
        <Route path="/signup/1" element={<Step1Handoff />} />
        <Route path="/signup/2" element={<Step2Provider />} />
        <Route path="/signup/3" element={<Step3Consent />} />
        <Route path="/signup/4" element={<Step4Child />} />
        <Route path="/signup/5" element={<Step5Avatar />} />
        <Route path="/signup/6" element={<Step6Done />} />

        <Route path="/teacher" element={<TeacherLanding />} />
        <Route path="/teacher/sign-in" element={<TeacherSignIn />} />
        <Route path="/teacher/dashboard" element={<TeacherDashboard />}>
          <Route index element={<Navigate to="lessons" replace />} />
          <Route path="lessons" element={<AuthoringTab />} />
          <Route path="live" element={<LiveTab />} />
          <Route path="review" element={<ReviewTab />} />
        </Route>

        <Route path="/join" element={<StudentJoin />} />

        <Route path="/tier" element={<TierPicker />} />
        <Route path="/builders" element={<BuildersComingSoon />} />
        <Route path="/designers" element={<InventorsComingSoon />} />

        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
